import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { Observable, forkJoin, BehaviorSubject } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import { UtilService } from 'src/app/shared/services/util.service';

@Injectable({
  providedIn: 'root'
})
export class CommonBookingService {

  private commConfig: any;
  private fetchCommConfigObservable: Observable<any>;
  private savedAddressesForRiders$ = new BehaviorSubject(null);
  private paymentType$ = new BehaviorSubject(null);
  public selectedPayer$ = new BehaviorSubject(null);

  savedAddressesForRiders = this.savedAddressesForRiders$.asObservable();
  paymentType = this.paymentType$.asObservable();
  selectedPayer = this.selectedPayer$.asObservable();

  constructor(private apiService: ApiService, private authService: AuthService, private commonService: CommonService,
              private utilService: UtilService) {}

  public fetchBookings(queryString: any, body: any): Observable<any[]> {

    const url = this.authService.isUserVendor() ? 'fetchVendorBookings' : 'fetchBookings';
    if (this.authService.isUserVendor()) {
      body['client'] = this.authService.client.client;
    }

    return this.apiService.httpRequest({
      url,
      type: 'POST',
      body,
      queryParams: queryString,
    });
  }

  fetchBookingByBookingList(bookingList: string[]) {
    return this.apiService.httpRequest({
      url: 'fetchFilteredBooking',
      type: 'POST',
      body: bookingList
    });
  }

  public fetchBookingSummaryData(body: any){
    // TODO do it differently for vendor login
    const url = 'fetchBookingsSummary';

    return this.apiService.httpRequest({
      url,
      type: 'POST',
      body
    });
  }

  public fetchCommTemplates(): any {
    if (this.commConfig) {
      return new Observable(observer => {
        observer.next(this.commConfig);
        observer.complete();
      });
    } else {
      if (!this.fetchCommConfigObservable) {
        this.fetchCommConfigObservable = new Observable(observer => {
          const httpRequest = this.apiService.httpRequest({
            url: 'fetchCommTemplates',
            type: 'GET',
            queryParams: {
              buid: this.authService.user.buId,
              empGuid: this.authService.user.employeeGuid,
              client: this.authService.client.client
            }
          });
          httpRequest.subscribe((response) => {
            this.fetchCommConfigObservable = null;
            this.commConfig = response;
            observer.next(this.commConfig);
            observer.complete();
          }, (error) => {
            observer.error(error);
          });
        });
      }
      return this.fetchCommConfigObservable;
    }
  }

  public sendCommunication(request) {
    return this.apiService.httpRequest({
      url: 'sendCommunication',
      type: 'POST',
      body: request
    });
  }

  public addCab(request: any) {
    return this.apiService.httpRequest({
      url: 'addCab',
      type: 'POST',
      body: request
    });
  }

  public cancelBooking(request: any): Observable<any[]> {
    return this.apiService.httpRequest({
      url: 'cancelBooking',
      type: 'DELETE',
      body: request
    });
  }

  public updateGeocode(request): Observable<any[]> {
    return this.apiService.httpRequest({
      url: 'updateGeoCords',
      type: 'POST',
      body: request
    });
  }

  public getBookingTrack(request): Observable<any[]> {
    return this.apiService.httpRequest({
      url: 'getTrackInfo',
      type: 'POST',
      body: request
    });
  }

  public fetchBookingDetails(searchQuery: any): Observable<any[]> {
    return this.apiService.httpRequest({
      url: 'fetchBookingDetails',
      type: 'GET',
      queryParams: searchQuery
    });
  }

  public fetchRequestDetails(searchQuery: any): Observable<any[]> {
    return this.apiService.httpRequest({
      url: 'fetchRequestDetails',
      type: 'GET',
      queryParams: searchQuery
    });
  }

  public clearService() {
    this.commConfig = undefined;
  }

  public getAdditionalDataForBookings(): Observable<any[]> {
    const editCutOffDetailsObservable = this.commonService.fetchEditConfig();
    const cancelCutOffDetailsObservable = this.commonService.fetchCancelConfig();
    const disableActionsConfigObservable = this.commonService.fetchDisableActionsConfig();

    // Observable.forkJoin (RxJS 5) changes to just forkJoin() in RxJS 6
    return forkJoin([editCutOffDetailsObservable, cancelCutOffDetailsObservable, disableActionsConfigObservable]);
  }

  public getBookingGarage(searchQuery: any): Observable<any> {
    return this.apiService.httpRequest({
      url: 'getBookingGarage',
      type: 'GET',
      queryParams: searchQuery
    });
  }

  public getGarageOptions(searchQuery: any): Observable<any> {
    return this.apiService.httpRequest({
      url: 'getGarageOptions',
      type: 'GET',
      queryParams: searchQuery
    });
  }

  public updateBookingGarage(request: any): Observable<any> {
    return this.apiService.httpRequest({
      url: 'updateBookingGarage',
      type: 'POST',
      body: request
    });
  }

  public isBookingEditDisabled(booking: any, editCutOffDetails: any) {
    let isActive = false;
    let isValidTime = false;
    let isWithinCutoff = false;

    if (booking.bookingStatus !== 'CANCELLED') {
      isActive = true;
    }

    // check date-time cutoffs
    if (this.utilService.checkIfDateTimeBetween(editCutOffDetails.start, editCutOffDetails.end)) {
      isValidTime = true;
    }

    const timeDiff = this.utilService.findTimeDiffInMinutes(booking.startTime);
    if (timeDiff > editCutOffDetails.cutOffMins) {
      isWithinCutoff = true;
    }

    return (isActive && isValidTime && isWithinCutoff) || booking.backDatedBooking;
  }

  public isBookingCancelDisabled(booking: any, cancelCutOffDetails: any) {
    let isActive = false;
    let isValidTime = false;
    let isWithinCutoff = false;
    const bookingInfo = booking;

    if (bookingInfo.bookingStatus !== 'CANCELLED') {
      isActive = true;
    }

    // check date-time cutoffs
    if (this.utilService.checkIfDateTimeBetween(cancelCutOffDetails.start, cancelCutOffDetails.end)) {
      isValidTime = true;
    }

    const timeDiff = this.utilService.findTimeDiffInMinutes(bookingInfo.startTime);
    if (timeDiff > cancelCutOffDetails.cutOffMins) {
      isWithinCutoff = true;
    }

    return isActive && isValidTime && isWithinCutoff;
  }

  updateHomeAddressesOfRiders(address) {
    this.savedAddressesForRiders$.next(address);
  }
  getPaymentType(paymentType) {
    this.paymentType$.next(paymentType);
  }
  getSelectedPayer(selectedPayer) {
    this.selectedPayer$.next(selectedPayer);
    return this.selectedPayer$;
  }

  getCabAllocationStatus(booking: any) {
    if (booking?.bookingStatus == 'CANCELLED') {
      return 'Cancelled';
    }
    if (booking?.tripStatus == 'TRIP_COMPLETED') {
      return 'Trip Completed';
    }
    if (booking?.tripStatus == 'DUTY_COMPLETED') {
      return 'Duty Completed';
    }
    if (booking?.tripStatus == 'ON_TRIP') {
      return 'In Progress';
    }
    if (booking?.cabAllocStatus == 'FINALIZED') {
      return 'Cab Allocated';
    }
    return 'Planned';
  }
}
